import { Flex } from '@chakra-ui/react'
import { ChakraPrimaryButton } from 'components/Button'
import Checkbox from 'components/CheckboxGroup/Checkbox'
import { AppointmentHoldCountdown } from 'components/Countdown/AppointmentHold'
import ModalWithConciergeEmailCTA from 'components/Modals/modalWithConciergeCTA'
import ProviderDetails from 'components/Scheduling/ProviderDetails'
import TimingDetails from 'components/Scheduling/TimingDetails'
import LoadingState from 'components/Spinner'
import { RoutePaths } from 'containers/Core/Routes'
import { useOnboardingContext } from 'context/OnboardingContext/OnboardingProvider'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import { useSourceContext } from 'context/SourceContext/SourceProvider'
import withAccount, { WithAccountProps } from 'hoc/withAccount'
import useElation from 'hooks/useElation'
import useSearchParams from 'hooks/useSearchParams'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Redirect } from 'react-router-dom'
import { AppointmentBookDoneProps } from 'utils/amplitude.types'
import { Amplitude } from 'utils/amplitude.utils'
import { Logger } from 'utils/logger.utils'
import {
  isInitialMedical as isInitialMedicalUtils,
  useIsInsurancePatient,
} from 'utils/program.utils'
import LegalText from './LegalText'
import PaymentBox from './PaymentBox'
import SessionExpiredBanner from './sessionExpiredBanner'
import './styles.scss'

const getErrorModalInfo = (isReschedule: boolean) => {
  return {
    title: 'We ran into an error finalizing your booking',
    description: `Thanks for your patience! An error came up while ${
      isReschedule ? 'rescheduling' : 'booking'
    } your visit. Please email us at concierge@allarahealth.com so we can provide you with a little extra support.`,
    emailSubject: 'Cannot complete appointment booking in onboarding',
  }
}

const ConfirmScreen: React.FC<
  WithAccountProps & { daysSinceNearestSlot: number }
> = ({ user, daysSinceNearestSlot }) => {
  const {
    amplitudeBookingMetadata,
    provider,
    dateTime,
    timeZone,
    isReschedule,
    appointmentType,
    appointmentHoldTimeLeft,
    penaltyScore,
    nextScreen,
    previousScreen,
    bookAppointment,
    rescheduleAppointment,
  } = useSchedulingContext()
  const { source } = useSourceContext()
  const [expired, setExpired] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [confirmLegal, setConfirmLegal] = useState<boolean>(false)
  const isInsurancePatient = useIsInsurancePatient(user)
  const searchParams = useSearchParams()
  const urlSource = searchParams.get('source')
  const startTime = useMemo(() => {
    return Date.now()
  }, [])
  const { enableElationAccountCreationAtPayment } = useFlags()
  const { insuranceDetails, uploadInsuranceDetails } = useOnboardingContext()
  const { createElationAccount } = useElation()
  const [errorModal, setErrorModal] = useState<{
    title: string
    description: string
    emailSubject: string
  } | null>(null)

  const isInitialMedical = isInitialMedicalUtils(appointmentType ?? null)

  const shouldShowPayment =
    isInsurancePatient && isInitialMedical && urlSource === 'onboarding'

  const handleBook = useCallback(async () => {
    const logger = new Logger('ConfirmAppointmentBooking')
    setIsLoading(true)

    const handleAction = isReschedule ? rescheduleAppointment : bookAppointment

    if (shouldShowPayment) {
      const variantLabel = user?.waitlistUser.variantLabel ?? ''
      Amplitude.userEntersPayment(
        Math.round((Date.now() - startTime) / 1000),
        variantLabel
      )

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'Insurance Payment Captured',
        })
      }
    }

    if (enableElationAccountCreationAtPayment && insuranceDetails.current) {
      // Create Elation account and update insurance details
      const createdElationAccount = await createElationAccount()

      if (createdElationAccount) {
        // We handle this at this way because uploadInsuranceDetails does many things that i don't want to break (also at backend)
        await uploadInsuranceDetails()
      } else {
        logger.error(
          `There was an error creating the Elation account for user ${user.email}`,
          'ConfirmScreen.handleBook'
        )
        setErrorModal(getErrorModalInfo(isReschedule ?? false))
        setIsLoading(false)
        return
      }
    }

    handleAction()
      .then(async (appointment) => {
        const amplitudeEventProps: AppointmentBookDoneProps = {
          appointment_id: appointment?.id ?? '',
          appointment_time: dateTime?.toISOString() ?? '',
          appointment_type_name: appointmentType?.name ?? '',
          is_reschedule: isReschedule ?? false,
          days_since_nearest_slot: daysSinceNearestSlot,
          penalty_score: penaltyScore,
          state: user.waitlistUser.state.code,
          carrier: user.waitlistUser.insuranceProvider ?? '',
          ...amplitudeBookingMetadata,
        }
        Amplitude.appointmentBookDone(amplitudeEventProps)

        nextScreen()
      })
      .catch((error) => {
        logger.error(
          `Booking Appointment Error: ${{
            statusCode: error.status_code,
            message: error.error_message,
            appointmentTypeName: appointmentType?.name,
            appointmentType: isReschedule ? 'rescheduling' : 'booking',
          }}`
        )

        setErrorModal(getErrorModalInfo(isReschedule ?? false))
      })
      .finally(() => setIsLoading(false))
  }, [
    source,
    expired,
    appointmentType,
    timeZone,
    dateTime,
    provider,
    user.sourceHealthId,
  ])

  const handleConfirmLegal = useCallback(() => setConfirmLegal(!confirmLegal), [
    confirmLegal,
  ])

  useEffect(() => {
    const expiredTimeout = setTimeout(
      () => setExpired(true),
      1000 * 60 * Number(appointmentHoldTimeLeft.split(':')[0]) +
        1000 * Number(appointmentHoldTimeLeft.split(':')[1])
    )

    return () => clearTimeout(expiredTimeout)
  }, [])

  if (!appointmentType) {
    return <LoadingState />
  } else if (!provider || !dateTime || !timeZone) {
    return <Redirect to={RoutePaths.SCHEDULING} />
  }

  return (
    <div className="confirm screen-container">
      <h1
        style={{
          fontFamily: 'Instrument Serif',
          fontWeight: 400,
          fontSize: '32px',
          marginBottom: '0',
        }}
      >
        Confirm your appointment
      </h1>
      {expired && <SessionExpiredBanner />}
      <Flex className="appointment-details-container">
        <ProviderDetails provider={provider} />
        <TimingDetails
          date={dateTime}
          timeZone={timeZone}
          appointmentType={appointmentType}
        />
      </Flex>
      {shouldShowPayment && !expired && (
        <AppointmentHoldCountdown
          minutes={Number(appointmentHoldTimeLeft.split(':')[0])}
          seconds={Number(appointmentHoldTimeLeft.split(':')[1])}
          useExternalTime={true}
        />
      )}
      {shouldShowPayment && (
        <PaymentBox handleBook={handleBook} previousScreen={previousScreen} />
      )}
      <LegalText />
      {!shouldShowPayment && (
        <>
          <div>
            <Checkbox
              className="small cancellation-policy-checkbox"
              controlId="legal-confirm"
              checked={confirmLegal}
              onChange={handleConfirmLegal}
              label="Please confirm you understand and agree to our Cancellation and No Show Policy"
            />
          </div>

          <div>
            <ChakraPrimaryButton
              onClick={handleBook}
              variant="dark-blue"
              disabled={expired || isLoading || !confirmLegal}
              isLoading={isLoading}
            >
              Confirm
            </ChakraPrimaryButton>
          </div>
        </>
      )}
      {errorModal && (
        <ModalWithConciergeEmailCTA
          isOpen={errorModal !== null}
          onClose={() => setErrorModal(null)}
          title={errorModal.title}
          description={errorModal.description}
          emailSubject={errorModal.emailSubject}
        />
      )}
    </div>
  )
}

export default withAccount(ConfirmScreen)
